import { store } from 'createStore';
import type { MetricType } from 'store/settings/types';
import type {
  FlowInterface,
  PortInterface,
  PortOutputFlowInterface,
} from 'store/units/types';
import { DisplayGraphMode } from 'store/units/types';
import theme from 'theme/theme';
import { CONVERSIONS, convertToCurrentMetric } from 'util/conversions';
import { PSD_CALC_MODE } from './types';
import type {
  DatasetInterface,
  FlowCurvePointInterface,
} from './components/FlowCurve';

export const getPxxValues = (
  pxxOne: number | undefined,
  pxxTwo: number | undefined,
  pxxThree: number | undefined,
  outputFlow: PortOutputFlowInterface | undefined,
  psdCalcMode: string,
) => {
  const outputFlowPsd = outputFlow?.psd;
  const outputFlowSize = outputFlow?.size;
  if (
    typeof outputFlowSize === 'undefined' ||
    typeof outputFlowPsd === 'undefined'
  ) {
    return [];
  }

  return getPxxPercentArray(pxxOne, pxxTwo, pxxThree).map((p) =>
    getInterpolatedSizeFromPsd(outputFlow, p, psdCalcMode),
  );
};

export const getPxxPercentArray = (
  pxxOne: number | undefined,
  pxxTwo: number | undefined,
  pxxThree: number | undefined,
) =>
  [pxxOne, pxxTwo, pxxThree].reduce(
    (acc: number[], curr) => (typeof curr === 'number' ? [...acc, curr] : acc),
    [],
  );

export const getPsdFromFraction = (
  fractionOne: number | undefined,
  outputFlow: PortOutputFlowInterface | undefined,
  metricType: MetricType,
) => {
  if (typeof fractionOne !== 'number') {
    return;
  }

  const convertedFractionOne = convertToCurrentMetric({
    value: fractionOne,
    metricType,
    conversionType: CONVERSIONS.MILLIMETER,
  });

  if (typeof convertedFractionOne !== 'number') {
    return;
  }

  const closestSize = outputFlow?.size.find((s) => s >= convertedFractionOne);
  if (typeof closestSize !== 'number') {
    return;
  }

  const fractionIndex = outputFlow?.size.indexOf(closestSize);

  if (typeof fractionIndex !== 'number' || fractionIndex === -1) {
    return;
  }

  const fractionPsdValue = outputFlow?.psd[fractionIndex];
  if (typeof fractionPsdValue !== 'number') {
    return;
  }

  return Math.round(fractionPsdValue * 100);
};

export const getInterpolatedSizeFromPsd = (
  outputFlow: PortOutputFlowInterface | undefined,
  psdValue: number | undefined,
  psdCalcMode: string,
): FlowCurvePointInterface => {
  const psd = outputFlow?.psd;
  const size = outputFlow?.size;
  if (
    typeof outputFlow === 'undefined' ||
    typeof size === 'undefined' ||
    typeof psd === 'undefined' ||
    typeof psdValue === 'undefined'
  ) {
    return { x: 0, y: 0 };
  }
  const psdCalcModeFactor =
    psdCalcMode === PSD_CALC_MODE.PERCENT ? 100 : outputFlow.totalMass;
  const psdVal = psdValue / psdCalcModeFactor;

  let newSize: number | undefined = undefined;
  for (let i = 0; i < size.length; i++) {
    if (psd[i] >= psdVal) {
      if (i > 0) {
        newSize =
          size[i] -
          ((psd[i] - psdVal) / (psd[i] - psd[i - 1])) * (size[i] - size[i - 1]);
      } else {
        newSize =
          size[i] - ((psd[i] - psdVal) / (psd[i] - 0.0)) * (size[i] - 0.0);
      }
      break;
    }
  }

  if (typeof newSize === 'undefined') return { x: 0, y: 0 };
  return {
    x: Math.round(newSize * 100) / 100,
    y: psdVal * psdCalcModeFactor,
    interpolated: true,
  };
};

export const getInterpolatedPsdFromSize = (
  outputFlow: PortOutputFlowInterface | undefined,
  sizeValue: number | undefined,
): FlowCurvePointInterface => {
  const psd = outputFlow?.psd;
  const size = outputFlow?.size;

  if (
    typeof size === 'undefined' ||
    typeof psd === 'undefined' ||
    typeof sizeValue === 'undefined'
  ) {
    return { x: 0, y: 0 };
  }

  let newPsd: number | undefined = undefined;
  for (let i = 0; i < size.length; i++) {
    if (size[i] >= sizeValue) {
      if (i > 0) {
        newPsd =
          psd[i] -
          ((size[i] - sizeValue) * (psd[i] - psd[i - 1])) /
            (size[i] - size[i - 1]);
      } else {
        newPsd =
          psd[i] - ((size[i] - sizeValue) * (psd[i] - 0.0)) / (size[i] - 0.0);
      }
      break;
    }
  }

  if (typeof newPsd === 'undefined') return { x: sizeValue, y: 100 };
  return { x: sizeValue, y: Math.round(1000 * newPsd) / 10 };
};

export const getPsdData = (
  outputFlow: PortOutputFlowInterface | undefined,
  psdCalcMode: string,
) => {
  const outputFlowPsd = outputFlow?.psd;
  const outputFlowSize = outputFlow?.size;

  if (outputFlowPsd && outputFlowSize && outputFlow) {
    const newPsdData = outputFlowPsd.map((psdValue, index) => {
      const sizeValue = outputFlowSize[index];
      const metricType = store.getState().settingsState.metricType;
      const newSizeValue = convertToCurrentMetric({
        value: outputFlowSize[index],
        metricType,
        conversionType: CONVERSIONS.MILLIMETER,
        decimals: 5,
      });
      if (typeof newSizeValue !== 'undefined') {
        const tonCovertFactor =
          psdCalcMode === PSD_CALC_MODE.PERCENT ? 100 : outputFlow.totalMass;
        return { y: psdValue * tonCovertFactor, x: newSizeValue };
      } else {
        const tonCovertFactor =
          psdCalcMode === PSD_CALC_MODE.PERCENT ? 100 : outputFlow.totalMass;
        return { y: psdValue * tonCovertFactor, x: sizeValue };
      }
    });

    if (newPsdData) {
      return newPsdData;
    }
    return undefined;
  }
};

export const getFlowPropsDatasets = (
  outputPorts: PortInterface[] | undefined,
  inputPorts: PortInterface[] | undefined,
  graphMode: string,
  psdCalcMode: string,
  activePortIndex?: number,
) => {
  const datasets: DatasetInterface[] = [];
  if (typeof outputPorts !== 'undefined') {
    outputPorts.forEach((port, index) => {
      const portData = getPsdData(port.outputFlow, psdCalcMode);
      datasets.push({
        data: portData,
        borderColor:
          typeof activePortIndex === 'number' && index !== activePortIndex
            ? theme.graph.colors.secondaryLine
            : theme.graph.colors.primaryLine,
        backgroundColor: theme.graph.colors.background,
        lineTension: 0.1,
        cubicInterpolationMode: 'default',
      });
    });
  }
  if (
    graphMode !== DisplayGraphMode.DISCHARGE_ONLY &&
    typeof inputPorts !== 'undefined'
  ) {
    inputPorts.forEach((port, _index) => {
      const inPortData = getPsdData(port.outputFlow, psdCalcMode);
      datasets.push({
        data: inPortData,
        borderColor: theme.graph.colors.feedLine,
        backgroundColor: theme.graph.colors.background,
        lineTension: 0.1,
        cubicInterpolationMode: 'default',
      });
    });
  }
  return datasets;
};

export const getDatasetMinMaxX = (datasets: DatasetInterface[]) => {
  let smallestX = Number.MAX_SAFE_INTEGER;
  let biggestX = Number.MIN_SAFE_INTEGER;
  let foundMinMax = false;
  datasets.forEach((dataset) => {
    const data = dataset.data;
    if (data !== undefined && data.length > 0) {
      const smallest = data[0].x;
      const biggest = data[data.length - 1].x;
      foundMinMax = true;
      if (smallest < smallestX) {
        smallestX = smallest;
      }
      if (biggest > biggestX) {
        biggestX = biggest;
      }
    }
  });
  if (!foundMinMax) return { smallestX: 0, biggestX: 100 };
  return { smallestX, biggestX };
};

export const getPsdTableData = (
  inputFlow: Pick<FlowInterface, 'psd' | 'size'>,
) => {
  const psdTable: { col1: number; col2: number }[] = [];
  const psdData = inputFlow.psd.map((psdValue, index) => {
    const metricType = store.getState().settingsState.metricType;
    const sizeValue = convertToCurrentMetric({
      value: inputFlow.size[index],
      metricType,
      conversionType: CONVERSIONS.MILLIMETER,
    });
    psdTable.push({ col1: sizeValue ?? 0, col2: psdValue });
    return { x: sizeValue, y: psdValue };
  });
  return { psdTable, psdData };
};

export const prepareGraphData = (
  outputPorts: PortInterface[] | undefined,
  inputPorts: PortInterface[] | undefined,
  graphMode: string,
  psdCalcMode: string,
  activePortIndex: number | undefined,
  ticks?: number[] | undefined,
  options?: {
    isStorage?: boolean;
    isFeed?: boolean;
    showFeed?: boolean;
  },
) => {
  const isStorage = options?.isStorage ?? outputPorts?.length === 0;
  let effectiveGraphMode = isStorage
    ? DisplayGraphMode.FEED_AND_DISCHARGE
    : graphMode || DisplayGraphMode.DISCHARGE_ONLY;

  if (!isStorage && options?.showFeed === false) {
    effectiveGraphMode = DisplayGraphMode.DISCHARGE_ONLY;
  } else if (options?.showFeed === true) {
    effectiveGraphMode = DisplayGraphMode.FEED_AND_DISCHARGE;
  }

  const datasets = getFlowPropsDatasets(
    outputPorts,
    inputPorts,
    effectiveGraphMode,
    psdCalcMode,
    activePortIndex,
  );

  const { smallestX, biggestX } = getDatasetMinMaxX(datasets);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const upperLimit = options?.isFeed ? ticks?.at(-1) || biggestX : biggestX;

  const metricType = store.getState().settingsState.metricType;
  const convertedTicks =
    metricType.value === 'IMPERIAL' || options?.isFeed
      ? ticks?.map((tick) => {
          const newTick = convertToCurrentMetric({
            value: tick,
            metricType,
            conversionType: CONVERSIONS.MILLIMETER,
          });
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          return newTick || tick;
        })
      : ticks;

  return {
    datasets,
    effectiveGraphMode,
    lowerLimit: smallestX,
    upperLimit,
    convertedTicks,
  };
};
